import React, { useEffect, useState } from 'react';
import { SeasonAccessUpdateInput } from '../../services/seasonAccessService';

interface EditSeasonAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: SeasonAccessUpdateInput) => Promise<void>;
  seasonAccess: any | null;
}

const EditSeasonAccessModal: React.FC<EditSeasonAccessModalProps> = ({ isOpen, onClose, onSave, seasonAccess }) => {
  const [formData, setFormData] = useState<SeasonAccessUpdateInput>({
    access_type: '',
    granted_by: null,
    active: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (seasonAccess) {
      setFormData({
        access_type: seasonAccess.access_type,
        granted_by: seasonAccess.granted_by ?? null,
        active: seasonAccess.active ?? true
      });
      setErrors({});
    }
  }, [seasonAccess]);

  const handleInputChange = (field: keyof SeasonAccessUpdateInput, value: string | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.access_type) e.access_type = 'Access type is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !seasonAccess) return;
    setIsSaving(true);
    try {
      await onSave(formData);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !seasonAccess) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit Season Access</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 focus:outline-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Season</label>
            <input value={seasonAccess.season_name || ''} readOnly disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
            <input value={seasonAccess.organisation_name || ''} readOnly disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Access Type *</label>
            <select value={formData.access_type || ''} onChange={e => handleInputChange('access_type', e.target.value)} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.access_type ? 'border-red-300' : 'border-gray-300'}`}>
              <option value="">Select access type...</option>
              <option value="viewer">Viewer</option>
              <option value="participant">Participant</option>
              <option value="owner">Owner</option>
            </select>
            {errors.access_type && <p className="text-red-500 text-xs mt-1">{errors.access_type}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Granted By</label>
            <input value={formData.granted_by || ''} onChange={e => handleInputChange('granted_by', e.target.value || null)} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter grantor (optional)" />
          </div>
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">Active</label>
            <button type="button" onClick={() => handleInputChange('active', !formData.active)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.active ? 'bg-blue-600' : 'bg-gray-200'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.active ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-xs text-gray-500">{formData.active ? 'Active' : 'Inactive'}</span>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">Cancel</button>
            <button type="submit" disabled={isSaving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md">{isSaving ? 'Saving...' : 'Update Data'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSeasonAccessModal;



